import { Model } from '@/models/Model';
import { QueryKey, useQueryClient } from '@tanstack/react-query';
import { useMemo } from 'react';
import {
  CacheManager,
  InfiniteCacheManager,
  ListCacheManager,
} from './CacheFacade';
import { CacheType } from './strategy/CacheStrategyFactory';
import useStableValue from '@/data/hook/shared/useStableValue';

export function useInfiniteCache<T extends Model>(queryKey: QueryKey) {
  const queryClient = useQueryClient();
  const stableKey = useStableValue(queryKey);
  return useMemo(
    () =>
      new InfiniteCacheManager<T>({
        queryKey: stableKey,
        cacheType: CacheType.INFINITE,
        queryClient,
      }),
    [stableKey, queryClient],
  );
}

export function useListCache<T extends Model>(queryKey: QueryKey) {
  const queryClient = useQueryClient();
  const stableKey = useStableValue(queryKey);
  return useMemo(
    () =>
      new ListCacheManager<T>({
        queryKey: stableKey,
        cacheType: CacheType.LIST,
        queryClient,
      }),
    [stableKey, queryClient],
  );
}

export function useSingleCache<T extends Model>(queryKey: QueryKey) {
  const queryClient = useQueryClient();
  const stableKey = useStableValue(queryKey);
  return useMemo(
    () =>
      new CacheManager<T>({
        queryKey: stableKey,
        cacheType: CacheType.SINGLE,
        queryClient,
      }),
    [stableKey, queryClient],
  );
}
